/**
 * Token Debug Utility
 * This utility helps debug token-related issues
 */

import jwtDecode from "jwt-decode";
import config from "../config";

class TokenDebugger {
  static logTokenInfo() {
    const accessToken = localStorage.getItem(config.AUTH_SESSION_KEY);
    const refreshToken = localStorage.getItem(config.REFRESH_TOKEN_KEY);
    
    console.group('🔐 Token Debug Info');
    
    // Access Token Info
    if (accessToken) {
      try {
        const decoded = jwtDecode(accessToken);
        const currentTime = Date.now() / 1000;
        const isExpired = decoded.exp < currentTime;
        const timeUntilExpiry = decoded.exp - currentTime;
        
        console.log('✅ Access Token:', {
          exists: true,
          isExpired,
          expiresAt: new Date(decoded.exp * 1000).toLocaleString(),
          timeUntilExpiry: isExpired ? 'EXPIRED' : `${Math.floor(timeUntilExpiry / 60)} minutes`,
          user: decoded.sub || decoded.user_id || 'Unknown',
          scopes: decoded.scopes || decoded.permissions || 'None'
        });
      } catch (error) {
        console.error('❌ Invalid Access Token:', error.message);
      }
    } else {
      console.warn('⚠️ No Access Token found');
    }
    
    // Refresh Token Info
    if (refreshToken) {
      try {
        const decoded = jwtDecode(refreshToken);
        const currentTime = Date.now() / 1000;
        const isExpired = decoded.exp < currentTime;
        const timeUntilExpiry = decoded.exp - currentTime;
        
        console.log('✅ Refresh Token:', {
          exists: true,
          isExpired,
          expiresAt: new Date(decoded.exp * 1000).toLocaleString(),
          timeUntilExpiry: isExpired ? 'EXPIRED' : `${Math.floor(timeUntilExpiry / 60)} minutes`,
        });
      } catch (error) {
        console.error('❌ Invalid Refresh Token:', error.message);
      }
    } else {
      console.warn('⚠️ No Refresh Token found');
    }
    
    console.groupEnd();
  }

  static checkTokenExpiry() {
    const accessToken = localStorage.getItem(config.AUTH_SESSION_KEY);
    const refreshToken = localStorage.getItem(config.REFRESH_TOKEN_KEY);
    
    const result = {
      accessToken: { exists: false, valid: false, expired: true },
      refreshToken: { exists: false, valid: false, expired: true }
    };
    
    if (accessToken) {
      try {
        const decoded = jwtDecode(accessToken);
        const currentTime = Date.now() / 1000;
        result.accessToken = {
          exists: true,
          valid: true,
          expired: decoded.exp < currentTime,
          expiresIn: decoded.exp - currentTime
        };
      } catch (error) {
        result.accessToken = { exists: true, valid: false, expired: true };
      }
    }
    
    if (refreshToken) {
      try {
        const decoded = jwtDecode(refreshToken);
        const currentTime = Date.now() / 1000;
        result.refreshToken = {
          exists: true,
          valid: true,
          expired: decoded.exp < currentTime,
          expiresIn: decoded.exp - currentTime
        };
      } catch (error) {
        result.refreshToken = { exists: true, valid: false, expired: true };
      }
    }
    
    return result;
  }

  static clearAllTokens() {
    console.warn('🧹 Clearing all tokens...');
    localStorage.removeItem(config.AUTH_SESSION_KEY);
    localStorage.removeItem(config.REFRESH_TOKEN_KEY);
    localStorage.removeItem('user_profile');
    console.log('✅ All tokens cleared');
  }

  static simulateTokenRefresh() {
    console.log('🔄 Simulating token refresh...');
    const refreshToken = localStorage.getItem(config.REFRESH_TOKEN_KEY);
    
    if (!refreshToken) {
      console.error('❌ No refresh token available for simulation');
      return;
    }
    
    // This would trigger the actual refresh
    console.log('Refresh token available, triggering refresh...');
    // The actual refresh will be handled by the axios interceptor
  }

  static getDetailedStatus() {
    const tokens = this.checkTokenExpiry();
    const userProfile = localStorage.getItem('user_profile');
    
    return {
      ...tokens,
      userProfile: {
        exists: !!userProfile,
        data: userProfile ? JSON.parse(userProfile) : null
      },
      authHeaders: {
        hasAuthHeader: !!window.axios?.defaults?.headers?.common?.Authorization
      }
    };
  }
}

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.TokenDebugger = TokenDebugger;
}

export default TokenDebugger;
